/**
 * Creator lists (Your Feed, Creator Library): sorting and the numbers shown on cards (QA 2026-10-16).
 * A creator with no views data (every reel's views missing, e.g. after the fake-views repair of decision 63, so the
 * server scored them on likes or not at all) shows "–" for Peak Wise Score and Usual views, and sorts last for
 * "Highest Wise Score" / "Usual views": a Wise Score is views ÷ usual views, so a likes-based number isn't one.
 */
export interface SortableCreator {
  handle: string | null; fullName: string | null; scoreBasis: string | null; baselineValue: number | null; peakOutlier: number | null;
  reelCount: number; transcriptCount: number | null; lastScannedAt: number | null; savedAt?: number;
}

export type CreatorSort = 'recent' | 'peak' | 'reels' | 'views' | 'transcripts' | 'name';

export const hasViewsData = (c: Pick<SortableCreator, 'scoreBasis' | 'baselineValue'>) => c.scoreBasis !== 'likes' && c.baselineValue !== null;
/** The peak Wise Score to show, or null ("–", no tier emoji). */
export const peakOf = (c: SortableCreator): number | null => (hasViewsData(c) ? c.peakOutlier : null);
/** Usual views to show, or null. */
export const usualViewsOf = (c: SortableCreator): number | null => (hasViewsData(c) ? c.baselineValue : null);

/** Descending by a number; missing values always last; ties keep the list order. */
function desc<T>(list: T[], f: (c: T) => number | null): T[] {
  return list.map((c, i) => ({ c, i, v: f(c) })).sort((a, b) => (a.v === null ? (b.v === null ? a.i - b.i : 1) : b.v === null ? -1 : b.v - a.v || a.i - b.i)).map((x) => x.c);
}

export function sortCreators<T extends SortableCreator>(list: T[], sort: CreatorSort, o: { nameBy?: 'name' | 'handle' } = {}): T[] {
  if (sort === 'peak') return desc(list, peakOf);
  if (sort === 'reels') return desc(list, (c) => c.reelCount);
  if (sort === 'views') return desc(list, usualViewsOf);
  if (sort === 'transcripts') return desc(list, (c) => c.transcriptCount ?? 0);
  if (sort === 'name') {
    const key = (c: T) => (o.nameBy === 'handle' ? c.handle ?? '' : c.fullName || c.handle || '');
    return [...list].sort((a, b) => key(a).localeCompare(key(b)));
  }
  return desc(list, (c) => c.lastScannedAt ?? c.savedAt ?? 0);
}
